import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { FormLabel } from '@components/form-label';
import { HideContainer } from '@components/hide-container';
import { IconForm } from '@components/icons/form';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useTravellerJourneyFormSubmission } from 'api/traveller-journeys/hooks';
import { ResponseContentV2 } from 'ui/actions/combo-send-form/response-content-v2';

type ExternalFormSidebarProps = {
  travellerJourneyId: string;
};

export const useExternalFormSidebar = () => {
  return useSidebar(ExternalFormSidebar, {
    variant: 'REGULAR',
  });
};

const ExternalFormSidebar = ({ travellerJourneyId }: ExternalFormSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading, isError } = useTravellerJourneyFormSubmission(travellerJourneyId);

  if (isLoading) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader icon={IconForm} iconColor="elementBlue100" title={t('title')} />
        <Sidebar.Content>
          <Spinner />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  if (isError || !data) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader icon={IconForm} iconColor="elementBlue100" title={t('title')} />
        <Sidebar.Content>
          <Text font="regular14" color="textSecondary" text={t('noSubmissionData')} />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader icon={IconForm} iconColor="elementBlue100" title={t('title')} />
      <Sidebar.Content>
        <Stack space={6}>
          <HideContainer title={t('triggerDetails')}>
            <Stack space={4}>
              <div>
                <FormLabel text={t('submitted')} />
                <Text
                  font="regular14"
                  text={abbreviateDate(data.dateSubmitted, 'en', 'datetime')}
                />
              </div>
              <div>
                <FormLabel text={t('source')} />
                <Text font="regular14" text={t('externalFormSubmission')} />
              </div>
              <div>
                <FormLabel text={t('autoStart')} />
                <Text font="regular14" text={data.isAutoStart ? t('enabled') : t('disabled')} />
              </div>
            </Stack>
          </HideContainer>
          <Separator />
          <HideContainer title={t('responses')}>
            <ResponseContentV2 formAnswers={data.formAnswers} />
          </HideContainer>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button text={t('close')} variant="secondary" onClick={hideSidebar} />
        </ButtonGroup>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const locale = {
  en: {
    title: 'External form submission',
    triggerDetails: 'Trigger details',
    submitted: 'Submitted',
    source: 'Source',
    externalFormSubmission: 'External form submission',
    autoStart: 'Auto-start journey on form submission',
    enabled: 'Enabled',
    disabled: 'Disabled',
    responses: 'Responses',
    noSubmissionData:
      'Submission data is not available for journeys started before this feature was enabled.',
    close: 'Close',
  },
};
